// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NotificationList, {
    NotificationListPresentationProps,
} from "@store/Library/Components/Notifications/NotificationList";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type NotificationTab = "unread" | "read";

export interface NotificationsPanelPresentationProps extends ExpandStyles<NotificationsPanelStyles> {}

export interface NotificationsPanelProps extends NotificationsPanelPresentationProps {
    /** All notifications, newest first. */
    messages: MessageModel[];
    unreadCount: number;
    isLoading: boolean;
    hasLoaded: boolean;
    /** Session language, for localized relative time. */
    language?: string;
    /** Page type appended to each action link as `returnPage` (see NotificationItem). */
    actionReturnPageType?: string;
    /** Called when an action link is clicked (before navigation) — used to close the flyout/panel. */
    onActionClick?: () => void;
    /** Flip the read status of one or many notifications. */
    onSetReadStatus: (messages: MessageModel[], isRead: boolean) => void;
}

export interface NotificationsPanelStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    titleRow?: InjectableCss;
    title?: TypographyPresentationProps;
    bulkActionLink?: LinkPresentationProps;
    tabStrip?: InjectableCss;
    tab?: ClickablePresentationProps;
    divider?: InjectableCss;
    listRegion?: InjectableCss;
    loadingWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    notificationList?: NotificationListPresentationProps;
}

export const notificationsPanelStyles: NotificationsPanelStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            /* min-height:0 + flex lets only the list region scroll, keeping header + tabs fixed. */
            min-height: 0;
            flex: 1 1 auto;
        `,
    },
    header: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    titleRow: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: ${pxToRem(12)};
            padding: ${pxToRem(20, 23, 0)};
        `,
    },
    title: {
        forwardAs: "h2",
        css: css`
            margin: 0;
            font-size: ${pxToRem(20)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    bulkActionLink: {
        color: "text.link",
        css: css`
            flex: 0 0 auto;
            white-space: nowrap;
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    tabStrip: {
        css: css`
            display: flex;
            gap: ${pxToRem(20)};
            padding: ${pxToRem(12, 23, 0)};
        `,
    },
    tab: {
        css: css`
            ${focusStyles()}
            padding-bottom: ${pxToRem(8)};
            border-bottom: 2px solid transparent;
            color: ${getColor("text.light")};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            transition: ${transitionHoverStyles("color", "border-color")};

            &:hover,
            &:active,
            &.on {
                color: ${getColor("primary")};
            }

            &.on {
                border-color: currentColor;
            }
        `,
    },
    divider: {
        css: css`
            flex: 0 0 auto;
            height: 1px;
            background-color: ${getColor("common.borderLight")};
        `,
    },
    listRegion: {
        css: css`
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
        `,
    },
    loadingWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: ${pxToRem(32)};
        `,
    },
};

/**
 * Presentational body of the Notifications surface (header + Unread/Read tabs + scrollable list),
 * shared by the desktop flyout and the mobile panel — only the outer chrome differs.
 */
const NotificationsPanel: FC<NotificationsPanelProps> = ({
    messages,
    unreadCount,
    isLoading,
    hasLoaded,
    language,
    actionReturnPageType,
    onActionClick,
    onSetReadStatus,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(notificationsPanelStyles, extendedStyles, overridedStyles);
    const [activeTab, setActiveTab] = useState<NotificationTab>("unread");
    const visibleMessages = messages.filter(message => (activeTab === "unread" ? !message.isRead : message.isRead));

    const handleBulkAction = () => {
        // On the Unread tab "Mark All as Read" (isRead=true); on the Read tab "Mark All as Unread".
        onSetReadStatus(visibleMessages, activeTab === "unread");
    };

    const toggleRead = (message: MessageModel) => onSetReadStatus([message], !message.isRead);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="notifications_panel">
            <StyledWrapper {...styles.header}>
                <StyledWrapper {...styles.titleRow}>
                    <Typography {...styles.title}>{translate("Notifications")}</Typography>
                    {visibleMessages.length > 0 && (
                        <Link
                            {...styles.bulkActionLink}
                            onClick={handleBulkAction}
                            data-test-selector="notifications_bulkAction"
                        >
                            {activeTab === "unread" ? translate("Mark All as Read") : translate("Mark All as Unread")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.tabStrip} role="tablist">
                    <Clickable
                        {...styles.tab}
                        role="tab"
                        aria-selected={activeTab === "unread"}
                        className={activeTab === "unread" ? "on" : undefined}
                        onClick={() => setActiveTab("unread")}
                        data-test-selector="notifications_tabUnread"
                    >
                        {`${translate("Unread")} (${unreadCount})`}
                    </Clickable>
                    <Clickable
                        {...styles.tab}
                        role="tab"
                        aria-selected={activeTab === "read"}
                        className={activeTab === "read" ? "on" : undefined}
                        onClick={() => setActiveTab("read")}
                        data-test-selector="notifications_tabRead"
                    >
                        {translate("Read")}
                    </Clickable>
                </StyledWrapper>
                <StyledWrapper {...styles.divider} aria-hidden="true" />
            </StyledWrapper>
            <StyledWrapper {...styles.listRegion}>
                {isLoading && !hasLoaded ? (
                    <StyledWrapper {...styles.loadingWrapper}>
                        <LoadingSpinner {...styles.loadingSpinner} />
                    </StyledWrapper>
                ) : (
                    <NotificationList
                        messages={visibleMessages}
                        language={language}
                        onToggleRead={toggleRead}
                        onActionClick={onActionClick}
                        actionReturnPageType={actionReturnPageType}
                        {...styles.notificationList}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default NotificationsPanel;
